import React, { useState, useEffect, useRef } from 'react';
import Navbar from '../components/Navbar.jsx';
import Footer from '../components/Footer.jsx';
import Seo from '../components/Seo.jsx';

const contactCards = [
  {
    title: 'Sales',
    desc: 'Ticket sales and anything to do with our events',
    email: 'sales@drunkenlectures.com',
  },
  {
    title: 'Complaints',
    desc: 'Complaints & any compliments you may have for us',
    email: 'help@drunkenlectures.com',
  },
  {
    title: 'Returns',
    desc: 'Returns, orders and anything to do with our merch',
    email: 'returns@drunkenlectures.com',
  },
  {
    title: 'Marketing',
    desc: 'If you want to talk to us about material or images used and request takedowns',
    email: 'marketing@drunkenlectures.com',
  },
];

export default function ContactUs() {
  const [parallax, setParallax] = useState(0);
  const heroRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      if (heroRef.current) {
        const rect = heroRef.current.getBoundingClientRect();
        setParallax(rect.top * 0.35);
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div style={{
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
      background: '#FFFFFF',
      minHeight: '100vh',
      margin: 0,
    }}>
      <Seo title="Contact Us" description="Get in touch with Drunken Lectures for ticket sales, complaints, merch returns, or media requests." path="/contact" />
      <Navbar />

      {/* ── HERO ── */}
      <section
        ref={heroRef}
        style={{
          paddingTop: '72px',
          position: 'relative',
          height: '520px',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#1A0A02',
        }}
      >
        <img
          src="/Assets/Contact Us.jpg"
          alt="Contact Us"
          style={{
            position: 'absolute',
            top: '-30%',
            left: 0,
            right: 0,
            width: '100%',
            height: '160%',
            objectFit: 'cover',
            objectPosition: 'center center',
            transform: `translateY(${parallax}px)`,
            willChange: 'transform',
          }}
        />
        {/* Warm orange-brown gradient overlay matching site palette */}
        <div style={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(160deg, rgba(180,70,10,0.72) 0%, rgba(120,45,5,0.65) 40%, rgba(30,15,5,0.55) 100%)',
        }} />
        <h1 style={{
          position: 'relative',
          zIndex: 1,
          color: 'white',
          fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Helvetica Neue', Arial, sans-serif",
          fontSize: 'clamp(2.5rem, 7vw, 4rem)',
          fontWeight: '600',
          letterSpacing: '-0.02em',
          margin: 0,
          textAlign: 'center',
        }}>
          Contact Us
        </h1>
      </section>

      {/* ── HELP INTRO ── */}
      <section style={{
        padding: '5rem 1.5rem 1.5rem',
        background: '#FDF6EE',
        textAlign: 'center',
      }}>
        <p style={{
          fontSize: '0.95rem',
          fontWeight: '700',
          color: '#17140F',
          marginBottom: '0.5rem',
          letterSpacing: '0.01em',
        }}>
          Have any queries?
        </p>
        <h2 style={{
          fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Helvetica Neue', Arial, sans-serif",
          fontSize: 'clamp(2rem, 5vw, 3rem)',
          fontWeight: '600',
          color: '#17140F',
          margin: '0 0 1.25rem',
          letterSpacing: '-0.02em',
          lineHeight: 1.1,
        }}>
          We&apos;re here to help.
        </h2>
        {/* Divider line */}
        <div style={{
          width: '48px',
          height: '3px',
          background: '#E8672E',
          borderRadius: '2px',
          margin: '0 auto',
        }} />
      </section>

      {/* ── CONTACT CARDS ── */}
      <section style={{
        padding: '3rem 1.5rem 6rem',
        background: '#FDF6EE',
      }}>
        <div style={{
          maxWidth: '760px',
          margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '1.5rem',
        }}>
          {contactCards.map((card) => (
            <div
              key={card.title}
              style={{
                background: 'white',
                borderRadius: '14px',
                padding: '2rem 1.75rem',
                textAlign: 'center',
                boxShadow: '0 2px 14px rgba(0,0,0,0.06)',
                border: '1px solid rgba(0,0,0,0.05)',
                transition: 'all 0.35s cubic-bezier(0.22,1,0.36,1)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 12px 32px rgba(0,0,0,0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 14px rgba(0,0,0,0.06)';
              }}
            >
              <h3 style={{
                fontSize: '1.15rem',
                fontWeight: '800',
                color: '#17140F',
                margin: '0 0 0.75rem',
              }}>
                {card.title}
              </h3>
              <p style={{
                fontSize: '0.92rem',
                color: '#6B665C',
                lineHeight: '1.6',
                margin: '0 0 1.1rem',
              }}>
                {card.desc}
              </p>
              <a
                href={`mailto:${card.email}`}
                style={{
                  color: '#E8672E',
                  fontWeight: '600',
                  fontSize: '0.92rem',
                  textDecoration: 'none',
                  transition: 'color 0.2s ease',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.color = '#D94F18'; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = '#E8672E'; }}
              >
                {card.email}
              </a>
            </div>
          ))}
        </div>
      </section>

      <Footer />

      <style>{`
        * { box-sizing: border-box; }
        body { margin: 0; padding: 0; }
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(24px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
